import React from 'react';
import { ArrowUpRight, TrendingUp, Users, Zap } from 'lucide-react';

const CaseStudies = () => {
  const caseStudies = [
    {
      category: 'E-Commerce',
      title: 'Mobile App Install Campaign',
      description: 'Drove 250K+ quality app installs for a leading e-commerce platform through programmatic and social channels.',
      image: 'https://images.pexels.com/photos/6476808/pexels-photo-6476808.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      metrics: [
        { icon: TrendingUp, label: 'ROI Increase', value: '340%' },
        { icon: Users, label: 'User Acquisition', value: '250K+' },
        { icon: Zap, label: 'Cost Reduction', value: '42%' },
      ],
      tags: ['Programmatic', 'Mobile', 'Social'],
    },
    {
      category: 'Lead Generation',
      title: 'B2B Lead Generation Campaign',
      description: 'Generated high-quality leads for a SaaS platform using targeted display, native, and email marketing strategies.',
      image: 'https://images.unsplash.com/photo-1703113690885-8caf0c77a7cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwdGVjaG5vbG9neSUyMGNvbm5lY3Rpdml0eXxlbnwwfHx8fDE3NzE3NDg3MzB8MA&ixlib=rb-4.1.0&q=85',
      metrics: [
        { icon: TrendingUp, label: 'Lead Quality', value: '85%' },
        { icon: Users, label: 'Leads Generated', value: '12K+' },
        { icon: Zap, label: 'Conversion Rate', value: '28%' },
      ],
      tags: ['Display', 'Email', 'Native'],
    },
    {
      category: 'Omnichannel',
      title: 'Multi-Channel Performance Campaign',
      description: 'Executed integrated multi-channel strategy across programmatic, social, and mobile platforms for maximum reach.',
      image: 'https://images.unsplash.com/photo-1722596627369-a743837c7176?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzE3NDg3MzR8MA&ixlib=rb-4.1.0&q=85',
      metrics: [
        { icon: TrendingUp, label: 'Performance Lift', value: '285%' },
        { icon: Users, label: 'Reach', value: '5M+' },
        { icon: Zap, label: 'Engagement', value: '+67%' },
      ],
      tags: ['Multi-Channel', 'Programmatic', 'Social'],
    },
  ];

  return (
    <section id="case-studies" className="py-24 relative bg-slate-950/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 backdrop-blur-sm mb-6">
            <span className="text-sm text-cyan-400 font-medium">Success Stories</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Proven Results That
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Drive Growth
            </span>
          </h2>
          <p className="text-xl text-slate-400 leading-relaxed">
            Real campaigns, real results. See how we've helped businesses achieve their performance goals.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {caseStudies.map((study, index) => (
            <div
              key={index}
              className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-800/50 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all duration-300"
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={study.image}
                  alt={study.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-block bg-cyan-500/20 backdrop-blur-sm border border-cyan-500/30 rounded-full px-3 py-1 text-xs font-medium text-cyan-300">
                    {study.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Title & Description */}
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                    {study.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {study.description}
                  </p>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-800/50">
                  {study.metrics.map((metric, idx) => {
                    const Icon = metric.icon;
                    return (
                      <div key={idx} className="text-center">
                        <Icon className="w-4 h-4 text-cyan-400 mx-auto mb-2" />
                        <p className="text-lg font-bold text-white">{metric.value}</p>
                        <p className="text-xs text-slate-400 mt-1">{metric.label}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800/50">
                  {study.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs text-slate-300 bg-slate-800/50 px-3 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hover Arrow */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-cyan-500 rounded-full p-2">
                  <ArrowUpRight className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-slate-400 mb-4">
            Want to see similar results for your business?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-medium transition-colors duration-200 group"
          >
            Let's discuss your goals
            <ArrowUpRight className="ml-2 w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
